import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { blockchainAPI } from '../services/api';
import './VerifyPage.css';

function VerifyPage() {
  const { hash } = useParams();

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [manualHash, setManualHash] = useState('');
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (hash) {
      fetchVerification(hash);
    } else {
      setLoading(false);
    }
  }, [hash]);

  const fetchVerification = async (h) => {
    setLoading(true);
    try {
      const res = await blockchainAPI.verify(h);
      setResult(res.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setResult({ verified: false, error: 'No certificate found for this hash' });
      } else {
        setResult({ verified: false, error: 'Verification failed. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!manualHash.trim()) return;
    setSearching(true);
    await fetchVerification(manualHash.trim());
    setSearching(false);
  };

  return (
    <div className="vp-page">
      <div className="vp-container">
        <div className="vp-brand">VoiceMeet Pro</div>

        <div className="vp-header">
          <h1>Recording Verification</h1>
          <p>Enter a document hash to verify the authenticity of a VoiceMeet Pro recording</p>
        </div>

        {!hash && (
          <form className="vp-form" onSubmit={handleSearch}>
            <input
              className="vp-input"
              type="text"
              placeholder="Paste document hash (SHA-256)…"
              value={manualHash}
              onChange={e => setManualHash(e.target.value)}
            />
            <button className="vp-btn" type="submit" disabled={searching}>
              {searching ? 'Verifying…' : 'Verify'}
            </button>
          </form>
        )}

        {loading && (
          <div className="vp-loading">
            <div className="vp-spinner" />
            <p>Checking blockchain records…</p>
          </div>
        )}

        {!loading && result && (
          <motion.div
            className={`vp-result ${result.verified ? 'vp-verified' : 'vp-not-found'}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {result.verified ? (
              <>
                <div className="vp-result-icon">✓</div>
                <h2>Authentic Recording</h2>
                <p className="vp-result-sub">
                  This recording has been cryptographically anchored on the Bitcoin blockchain.
                </p>

                <div className="vp-details">
                  <div className="vp-detail-row">
                    <span className="vp-label">Meeting</span>
                    <span className="vp-detail-val">{result.meeting_title}</span>
                  </div>
                  {result.participants?.length > 0 && (
                    <div className="vp-detail-row">
                      <span className="vp-label">Participants</span>
                      <span className="vp-detail-val">{result.participants.join(', ')}</span>
                    </div>
                  )}
                  {result.duration_seconds && (
                    <div className="vp-detail-row">
                      <span className="vp-label">Duration</span>
                      <span className="vp-detail-val">{Math.floor(result.duration_seconds / 60)} min</span>
                    </div>
                  )}
                  <div className="vp-detail-row">
                    <span className="vp-label">Issued</span>
                    <span className="vp-detail-val">{new Date(result.issued_at).toUTCString()}</span>
                  </div>
                  <div className="vp-detail-row">
                    <span className="vp-label">Bitcoin Status</span>
                    <span className={`vp-detail-val ${result.status === 'confirmed' ? 'vp-confirmed-text' : 'vp-pending-text'}`}>
                      {result.status === 'confirmed'
                        ? `Confirmed${result.bitcoin_block ? ` · Block #${result.bitcoin_block.toLocaleString()}` : ''}`
                        : 'Pending confirmation (~1 hr)'}
                    </span>
                  </div>
                  <div className="vp-detail-row">
                    <span className="vp-label">Document Hash</span>
                    <code className="vp-code">{result.document_hash}</code>
                  </div>
                  <div className="vp-detail-row">
                    <span className="vp-label">Audio File Hash</span>
                    <code className="vp-code">{result.file_hash}</code>
                  </div>
                </div>

                <div className="vp-ots-note">
                  To independently verify the Bitcoin timestamp, visit{' '}
                  <a href="https://opentimestamps.org" target="_blank" rel="noreferrer">opentimestamps.org</a>{' '}
                  and upload the .ots proof file from the PDF certificate.
                </div>
              </>
            ) : (
              <>
                <div className="vp-result-icon vp-fail-icon">✗</div>
                <h2>Not Found</h2>
                <p className="vp-result-sub">{result.error}</p>
                <p className="vp-result-sub">No VoiceMeet Pro certificate exists for this hash. The recording may have been tampered with, or the hash may be incorrect.</p>
              </>
            )}
          </motion.div>
        )}

        <div className="vp-footer">
          <a href="https://voicemeet.techscriptaid.com">voicemeet.techscriptaid.com</a>
          {' · '}Blockchain anchoring powered by OpenTimestamps
        </div>
      </div>
    </div>
  );
}

export default VerifyPage;
